import {
  alpha,
  Autocomplete,
  Box,
  Chip,
  Stack,
  TextField,
  useTheme,
} from "@mui/material";
import { useMemo, useRef, useState, useCallback } from "react";

// Width of the dropdown input when chips are placed to its right, so the chips
// have room to flow beside a compact select rather than a full-width one.
const RIGHT_PLACEMENT_INPUT_WIDTH = 240;

type MultiSelectAutocompleteProps<T extends { readonly id: number | string }> =
  Readonly<{
    options: readonly T[];
    selectedIds: readonly T["id"][];
    onChange: (ids: readonly T["id"][]) => void;
    getOptionLabel: (option: T) => string;
    filterOption: (option: T, inputValue: string) => boolean;
    label: string;
    placeholder?: string;
    chipPlacement?: "below" | "inside" | "right";
    focusedId?: T["id"] | null;
    onToggleFocus?: (id: T["id"]) => void;
  }>;

export function MultiSelectAutocomplete<
  T extends { readonly id: number | string },
>({
  options,
  selectedIds,
  onChange,
  getOptionLabel,
  filterOption,
  label,
  placeholder,
  chipPlacement = "inside",
  focusedId,
  onToggleFocus,
}: MultiSelectAutocompleteProps<T>) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartId, setDragStartId] = useState<T["id"] | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  const optionsMap = useMemo(() => {
    return new Map(options.map((option) => [option.id, option]));
  }, [options]);

  const selectedOptions = useMemo(() => {
    return selectedIds
      .map((id) => optionsMap.get(id))
      .filter((option): option is T => option !== undefined);
  }, [selectedIds, optionsMap]);

  const filteredOptions = useMemo(() => {
    if (!inputValue) return options;
    return options.filter((option) => filterOption(option, inputValue));
  }, [options, inputValue, filterOption]);

  const handleMouseDown = useCallback((id: T["id"]) => {
    setIsDragging(true);
    setDragStartId(id);
  }, []);

  const handleMouseEnter = useCallback(
    (id: T["id"]) => {
      if (!isDragging || dragStartId === null) return;

      const startIndex = filteredOptions.findIndex((o) => o.id === dragStartId);
      const endIndex = filteredOptions.findIndex((o) => o.id === id);

      if (startIndex === -1 || endIndex === -1) return;

      const minIndex = Math.min(startIndex, endIndex);
      const maxIndex = Math.max(startIndex, endIndex);

      const rangeIds = filteredOptions
        .slice(minIndex, maxIndex + 1)
        .map((o) => o.id);

      const newSelectedIds = new Set(selectedIds);
      rangeIds.forEach((id) => newSelectedIds.add(id));

      onChange(Array.from(newSelectedIds));
    },
    [isDragging, dragStartId, filteredOptions, selectedIds, onChange]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setDragStartId(null);
  }, []);

  const handleOptionClick = useCallback(
    (id: T["id"], event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      const newSelectedIds = selectedIds.includes(id)
        ? selectedIds.filter((existingId) => existingId !== id)
        : [...selectedIds, id];

      onChange(newSelectedIds);
    },
    [selectedIds, onChange]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndex((prev) =>
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
      } else if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        if (
          highlightedIndex >= 0 &&
          highlightedIndex < filteredOptions.length
        ) {
          const selectedOption = filteredOptions[highlightedIndex];
          if (selectedOption) {
            const newSelectedIds = selectedIds.includes(selectedOption.id)
              ? selectedIds.filter((id) => id !== selectedOption.id)
              : [...selectedIds, selectedOption.id];
            onChange(newSelectedIds);
          }
        }
      } else if (event.key === "Escape") {
        setHighlightedIndex(-1);
      }
    },
    [filteredOptions, highlightedIndex, selectedIds, onChange]
  );

  const autocompleteInput = (
    <Box
      ref={autocompleteRef}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      sx={{ userSelect: isDragging ? "none" : "auto" }}
    >
      <Autocomplete
        multiple
        disableCloseOnSelect
        clearOnBlur={false}
        options={filteredOptions}
        value={selectedOptions}
        inputValue={inputValue}
        onInputChange={(_event, newInputValue) => {
          setInputValue(newInputValue);
          setHighlightedIndex(-1);
        }}
        getOptionLabel={getOptionLabel}
        onChange={(_event, newOptions) => {
          onChange(newOptions.map((option) => option.id));
        }}
        onBlur={() => {
          setInputValue("");
        }}
        renderOption={({ key, ...props }, option, { index }) => {
          const isSelected = selectedIds.includes(option.id);
          const isHighlighted = index === highlightedIndex;
          return (
            <li
              key={key}
              {...props}
              onMouseDown={(event) => {
                event.preventDefault();
                handleMouseDown(option.id);
              }}
              onMouseEnter={() => {
                handleMouseEnter(option.id);
                setHighlightedIndex(index);
              }}
              onClick={(event) => {
                handleOptionClick(option.id, event);
              }}
              style={{
                ...((props as React.HTMLAttributes<HTMLLIElement>).style ?? {}),
                backgroundColor: isHighlighted
                  ? alpha(theme.palette.primary.main, 0.24)
                  : isSelected
                    ? alpha(theme.palette.primary.main, 0.12)
                    : "transparent",
                cursor: isDragging ? "grabbing" : "pointer",
              }}
            >
              {getOptionLabel(option)}
            </li>
          );
        }}
        renderValue={chipPlacement === "inside" ? undefined : () => null}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={
              chipPlacement !== "inside" || selectedOptions.length === 0
                ? placeholder
                : undefined
            }
            onKeyDown={handleKeyDown}
          />
        )}
        size="small"
      />
    </Box>
  );

  const selectedChips = (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {selectedOptions.map((option) => {
        const isFocused = onToggleFocus && focusedId === option.id;
        return (
          <Chip
            key={option.id}
            label={getOptionLabel(option)}
            onClick={
              onToggleFocus
                ? () => {
                    onToggleFocus(option.id);
                  }
                : undefined
            }
            onDelete={() => {
              const newSelectedIds = selectedIds.filter(
                (id) => id !== option.id
              );
              onChange(newSelectedIds);
            }}
            color={isFocused ? "primary" : "default"}
            variant={isFocused ? "filled" : "outlined"}
            size="small"
          />
        );
      })}
    </Stack>
  );

  if (chipPlacement === "right") {
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        sx={{ flex: 1 }}
      >
        <Box sx={{ width: RIGHT_PLACEMENT_INPUT_WIDTH, flexShrink: 0 }}>
          {autocompleteInput}
        </Box>
        {selectedOptions.length > 0 && selectedChips}
      </Stack>
    );
  }

  return (
    <Box sx={{ flex: 1 }}>
      {autocompleteInput}
      {chipPlacement === "below" && selectedOptions.length > 0 && (
        <Box sx={{ mt: 1 }}>{selectedChips}</Box>
      )}
    </Box>
  );
}
